import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Plus, RefreshCw, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { SortableTable } from '@/components/ui/sortable-table';
import { formatCurrency } from '@/lib/format';
import { AccountEditor } from './account-editor';
import { useBusinessAccounts } from './business-accounts.api';
import {
  ENTITY_TYPE,
  businessTypeLabel,
  maskTail,
  toInput,
  emptyAccount,
  type BusinessAccount,
  type BusinessAccountInput,
} from './types';

/**
 * Payout accounts, one per store.
 *
 * The original's table had columns for the account number and the routing
 * number, printed in full. They are the last four digits here, and reading the
 * whole number is a deliberate click inside the editor — the API sends them in
 * the clear either way, but nothing is served by having them on screen while
 * somebody scans the list for a different store.
 */

const ANY = 'ANY';

export function BusinessAccountsPage() {
  const { t } = useTranslation();

  const [term, setTerm] = useState('');
  const [status, setStatus] = useState(ANY);
  const [payouts, setPayouts] = useState(ANY);
  const [editing, setEditing] = useState<{
    id: string | null;
    values: BusinessAccountInput;
  } | null>(null);

  const list = useBusinessAccounts();
  const accounts = useMemo(() => list.data ?? [], [list.data]);

  const needle = term.trim().toLowerCase();
  const rows = useMemo(
    () =>
      accounts.filter((account) => {
        if (status !== ANY && String(account.isActive) !== status) return false;
        if (payouts !== ANY && String(account.allowPayouts) !== payouts) {
          return false;
        }
        if (!needle) return true;
        return [
          account.businessName,
          account.accountName,
          account.accountHolderName,
          account.contactEmail,
        ]
          .filter(Boolean)
          .some((value) => value!.toLowerCase().includes(needle));
      }),
    [accounts, status, payouts, needle]
  );

  const fields: FilterField[] = [
    {
      id: 'status',
      label: t('businessAccounts.status'),
      value: status,
      onChange: setStatus,
      sort: false,
      resetValue: ANY,
      options: [
        { value: ANY, label: t('businessAccounts.anyStatus') },
        { value: 'true', label: t('businessAccounts.active') },
        { value: 'false', label: t('businessAccounts.inactive') },
      ],
    },
    {
      id: 'payouts',
      label: t('businessAccounts.payouts'),
      value: payouts,
      onChange: setPayouts,
      sort: false,
      resetValue: ANY,
      options: [
        { value: ANY, label: t('businessAccounts.anyPayouts') },
        { value: 'true', label: t('businessAccounts.payoutsOn') },
        { value: 'false', label: t('businessAccounts.payoutsOff') },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('businessAccounts.title')}
        description={t('businessAccounts.subtitle')}
        actions={
          <>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t('common.reload')}
              disabled={list.isFetching}
              onClick={() => void list.refetch()}
            >
              <RefreshCw className={list.isFetching ? 'animate-spin' : ''} />
            </Button>
            <Button
              size="sm"
              onClick={() => setEditing({ id: null, values: emptyAccount() })}
            >
              <Plus />
              <span className="hidden sm:inline">
                {t('businessAccounts.newAccount')}
              </span>
            </Button>
          </>
        }
      />

      <FilterBar
        busy={list.isFetching}
        search={
          <div className="relative flex-1">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={t('businessAccounts.searchPlaceholder')}
              aria-label={t('businessAccounts.searchPlaceholder')}
              className="h-10 pl-9"
            />
          </div>
        }
        fields={fields}
        summary={
          list.isSuccess ? (
            <p className="text-muted-foreground text-sm" aria-live="polite">
              {t('businessAccounts.showingOf', {
                shown: rows.length,
                total: accounts.length,
              })}
            </p>
          ) : null
        }
      />

      {list.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('businessAccounts.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void list.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      <SortableTable
        rows={rows}
        rowKey={(account) => account.id}
        loading={list.isPending}
        busy={list.isFetching}
        pageSize={25}
        emptyMessage={
          accounts.length === 0
            ? t('businessAccounts.none')
            : t('businessAccounts.noneMatch')
        }
        initialSort={{ key: 'store', direction: 'asc' }}
        columns={[
          {
            key: 'store',
            header: t('businessAccounts.store'),
            sortValue: (account) => account.businessName ?? '',
            render: (account: BusinessAccount) => (
              <div className="min-w-0">
                <p className="truncate font-medium">
                  {account.businessName || '—'}
                </p>
                {account.accountName && (
                  <p className="text-muted-foreground truncate text-xs">
                    {account.accountName}
                  </p>
                )}
              </div>
            ),
          },
          {
            key: 'holder',
            header: t('businessAccounts.accountHolder'),
            sortValue: (account) => account.accountHolderName ?? '',
            render: (account) => (
              <div className="min-w-0">
                <p className="truncate">{account.accountHolderName || '—'}</p>
                <p className="text-muted-foreground truncate text-xs">
                  {account.accountHolderType === ENTITY_TYPE.company
                    ? t('businessAccounts.company')
                    : t('businessAccounts.individual')}
                  {' · '}
                  {businessTypeLabel(account.businessType)}
                </p>
              </div>
            ),
          },
          {
            key: 'account',
            header: t('businessAccounts.bankAccount'),
            // Sorted on the visible digits, since that is what is compared.
            sortValue: (account) => account.accountNumber?.slice(-4) ?? '',
            render: (account) => (
              <span className="font-mono text-xs">
                {maskTail(account.accountNumber)}
              </span>
            ),
          },
          {
            key: 'minimum',
            header: t('businessAccounts.minimumPayout'),
            align: 'right',
            sortValue: (account) => account.minimumPayoutAmount,
            render: (account) => (
              <span className="tabular-nums">
                {formatCurrency(account.minimumPayoutAmount)}
              </span>
            ),
          },
          {
            key: 'payouts',
            header: t('businessAccounts.payouts'),
            sortValue: (account) =>
              (account.allowPayouts ? 2 : 0) + (account.isActive ? 1 : 0),
            render: (account) => (
              <div className="flex flex-wrap gap-1">
                <Badge variant={account.isActive ? 'success' : 'secondary'}>
                  {account.isActive
                    ? t('businessAccounts.active')
                    : t('businessAccounts.inactive')}
                </Badge>
                {account.allowPayouts ? (
                  <Badge variant="secondary">
                    {t('businessAccounts.payoutsOn')}
                  </Badge>
                ) : (
                  <Badge variant="outline">
                    {t('businessAccounts.payoutsOff')}
                  </Badge>
                )}
              </div>
            ),
          },
          {
            key: 'edit',
            header: '',
            align: 'right',
            sortValue: () => 0,
            render: (account) => (
              <Button
                size="sm"
                onClick={() =>
                  setEditing({ id: account.id, values: toInput(account) })
                }
              >
                {t('businessAccounts.edit')}
              </Button>
            ),
          },
        ]}
      />

      <AccountEditor
        account={editing}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      />
    </div>
  );
}
